import type { ReactNode } from 'react';

import { cn } from '../lib/cn';

export interface FieldProps {
  id: string;
  label: ReactNode;
  /** Helper text. Rendered above the error and referenced by `aria-describedby`. */
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** Hide the label visually but keep it for assistive tech. Use sparingly. */
  hideLabel?: boolean;
  className?: string;
  children: ReactNode;
}

export const hintId = (id: string) => `${id}-hint`;
export const errorId = (id: string) => `${id}-error`;

/** Build the `aria-describedby` value for a control from what is actually rendered. */
export function describedBy(id: string, hint?: unknown, error?: unknown): string | undefined {
  const ids = [hint ? hintId(id) : null, error ? errorId(id) : null].filter(Boolean);
  return ids.length ? ids.join(' ') : undefined;
}

/**
 * Label, hint and error for one control.
 *
 * Every control in this library renders a real `<label for>` pointing at a real input
 * id. Placeholder-as-label is not available, on purpose: it disappears the moment
 * someone types, and these forms carry fee agreements and limitation dates.
 *
 * The error is a live region, so a validation failure is announced rather than only
 * shown.
 */
export function Field({
  id,
  label,
  hint,
  error,
  required,
  hideLabel,
  className,
  children,
}: FieldProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label
        htmlFor={id}
        className={cn(
          'text-sm font-medium text-ink',
          hideLabel && 'sr-only',
        )}
      >
        {label}
        {required && (
          <span className="ml-0.5 text-danger" aria-hidden="true">
            *
          </span>
        )}
        {required && <span className="sr-only"> (required)</span>}
      </label>

      {children}

      {hint && (
        <p id={hintId(id)} className="text-xs text-ink-muted">
          {hint}
        </p>
      )}

      {error && (
        <p id={errorId(id)} role="alert" className="text-xs font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

/** Shared visual treatment for text-like inputs, so they cannot drift apart. */
export const controlClasses = (invalid?: boolean) =>
  cn(
    'block w-full rounded border bg-surface px-3 text-base text-ink shadow-sm',
    'transition-colors placeholder:text-ink-subtle',
    'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-ink-subtle',
    'read-only:bg-surface-sunken',
    invalid
      ? 'border-danger focus-visible:outline-danger'
      : 'border-line-strong hover:border-ink-subtle',
  );
